import React, { ReactNode } from 'react'
import { BaseView, BaseText } from '@/components/base'
import { Avatar } from '@/components/Avatar'
import { LinearGradient } from '@/components/LinearGradient'
import { AppIcons } from '@/components/AppIcons'
import { useTheme } from '@/hooks/useTheme'
import { responsiveAdapter } from '@/utils'
import { formatFiatCurrency } from '@/utils/currency/formatFiatCurrency'

type IconName = string

// Types
interface CardRootProps {
  children: ReactNode
  onPress?: () => void
  disabled?: boolean
  style?: React.CSSProperties
  borderOnlyTop?: boolean
  borderOnlyBottom?: boolean
  noBorder?: boolean
}

interface CardContentProps {
  children: ReactNode
  style?: React.CSSProperties
}

interface CardFooterProps {
  children: ReactNode
  style?: React.CSSProperties
}

interface CardCurrencyIconProps {
  uri?: string
  path?: string
  bgColor: string | [string, string, ...string[]]
  size?: number
  fallbackText?: string
  showWalletIcon?: boolean
}

interface CardTitleProps {
  title: string
  disabled?: boolean
  style?: React.CSSProperties
  numberOfLines?: number
}

interface CardDescriptionProps {
  description: string
  style?: React.CSSProperties
  numberOfLines?: number
}

interface CardRightIconProps {
  children: ReactNode
}

interface CardFlagProfitsOrLossesProps {
  percentChange24h?: number
  revenue24hFiatAmount?: number
  fiatCode?: string
}

interface CardAvatarProps {
  uri?: string
  path?: string
  name?: string
  size?: number
  iconName?: string
  iconSize?: number
  containerStyle?: React.CSSProperties
  imageStyle?: React.CSSProperties
}

interface CardAvatarGradientProps {
  name?: string
  iconName?: IconName
  colors?: string[]
  size?: number
  iconSize?: number
  style?: React.CSSProperties
  start?: [number, number]
  end?: [number, number]
  borderRadiusRatio?: number
}

// Components
function CardRoot({
  children,
  onPress,
  disabled = false,
  style,
  borderOnlyTop = false,
  borderOnlyBottom = false,
  noBorder = false,
}: CardRootProps) {
  const borderRadius =
    borderOnlyTop || borderOnlyBottom || noBorder ? 0 : responsiveAdapter(24)
  const borderSize = responsiveAdapter(28)
  const borderSizePx = `${borderSize}px`

  // Default styles
  const defaultStyles: React.CSSProperties = {
    flexDirection: 'row',
    alignItems: 'center',
    padding: `${responsiveAdapter(12)}px`,
    gap: '12px',
    cursor: !onPress ? 'default' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    ...(borderOnlyTop && {
      borderTopLeftRadius: borderSizePx,
      borderTopRightRadius: borderSizePx,
    }),
    ...(borderOnlyBottom && {
      borderBottomLeftRadius: borderSizePx,
      borderBottomRightRadius: borderSizePx,
    }),
  }

  return (
    <BaseView
      bgColor="background.cardItemRoot"
      rounded={borderRadius}
      style={{
        ...defaultStyles,
        ...style,
      }}
      onClick={onPress}
    >
      {children}
    </BaseView>
  )
}

function CardContent({ children, style }: CardContentProps) {
  return (
    <BaseView
      flex={1}
      style={{
        width: '100%',
        minWidth: 0, // Allows element to shrink for correct text truncation
        flexDirection: 'column',
        gap: '6px',
        alignItems: 'flex-start',
        ...style,
      }}
    >
      {children}
    </BaseView>
  )
}

function CardFooter({ children, style }: CardFooterProps) {
  return (
    <BaseView
      align="flex-end"
      style={{
        paddingLeft: `${responsiveAdapter(4)}px`,
        ...style,
      }}
    >
      {children}
    </BaseView>
  )
}

function CardCurrencyIcon({
  uri,
  path,
  bgColor,
  size = 48,
  fallbackText,
  showWalletIcon = false,
}: CardCurrencyIconProps) {
  const theme = useTheme()

  // Determine image source as in RN
  const imageSource = path || uri

  // Determine gradient colors as in RN version
  const gradientColors = Array.isArray(bgColor) ? bgColor : [bgColor, bgColor]

  // If we have only one color, duplicate it to create a gradient
  // If array, use as is (can be 2 or 3 colors)
  const finalColors =
    gradientColors.length === 1
      ? [gradientColors[0], gradientColors[0]]
      : gradientColors

  return (
    <LinearGradient
      colors={finalColors}
      locations={finalColors.length === 3 ? [0, 0.15, 0.8] : [0, 1]}
      start={{ x: 0.8, y: 0 }}
      end={{ x: 0.5, y: 1 }}
      circle
      circleSize={size}
      center
    >
      {imageSource ? (
        <img
          src={imageSource}
          alt={fallbackText || 'Currency'}
          style={{
            width: '100%', // Full width as in RN
            height: '28px', // Fixed height as in RN (h={28})
            objectFit: 'contain', // As in RN: contentFit="contain"
          }}
          onError={(e) => {
            // Fallback to text if image failed to load
            const target = e.target as HTMLImageElement
            target.style.display = 'none'
          }}
        />
      ) : (
        <BaseText
          fontSize={Math.max(12, size / 2.5)}
          fontFamily="semibold"
          color="white"
          style={{
            textAlign: 'center',
            fontWeight: '600',
          }}
        >
          {fallbackText || '?'}
        </BaseText>
      )}

      {/* Wallet icon as in RN */}
      {showWalletIcon && (
        <BaseView
          style={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            width: `${Math.round(size * 0.42)}px`, // Proportional to size (20/48 ≈ 0.42)
            height: `${Math.round(size * 0.42)}px`,
            borderRadius: `${Math.round(size * 0.125)}px`, // Proportional to size (6/48 ≈ 0.125)
            backgroundColor: theme.colors.background.screen,
            borderWidth: '2px',
            borderColor: theme.colors.background.cardItemRoot,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AppIcons name="archiveBox" size={16} />
        </BaseView>
      )}
    </LinearGradient>
  )
}

function CardTitle({
  title,
  disabled = false,
  style,
  numberOfLines,
}: CardTitleProps) {
  return (
    <BaseText
      fontSize={16}
      fontFamily="semibold"
      color={disabled ? 'label.disabled' : 'label.default'}
      numberOfLines={numberOfLines}
      style={{
        // Remove old CSS truncation styles when numberOfLines is used
        ...(numberOfLines
          ? {}
          : {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }),
        maxWidth: '100%',
        ...style,
      }}
    >
      {title}
    </BaseText>
  )
}

function CardDescription({
  description,
  style,
  numberOfLines,
}: CardDescriptionProps) {
  return (
    <BaseText
      fontSize={14}
      fontFamily="regular"
      color="label.description"
      numberOfLines={numberOfLines}
      style={style}
    >
      {description}
    </BaseText>
  )
}

function CardRightIcon({ children }: CardRightIconProps) {
  return (
    <BaseView
      style={{
        justifyContent: 'center',
        alignItems: 'flex-end',
        maxWidth: `${responsiveAdapter(150)}px`,
      }}
    >
      {children}
    </BaseView>
  )
}

function CardFlagProfitsOrLosses({
  percentChange24h = 0,
  revenue24hFiatAmount = 0,
  fiatCode = 'USD',
}: CardFlagProfitsOrLossesProps) {
  const theme = useTheme()

  if (percentChange24h === 0) return null

  const isProfit = percentChange24h > 0
  const handledPercentage = Math.abs(percentChange24h).toFixed(2)

  // Format profit/loss amount as in RN version
  const revenue24hFiatAmountWithoutMinus = String(revenue24hFiatAmount).replace(
    '-',
    '',
  )
  const formattedRevenue24hFiatAmount = revenue24hFiatAmount
    ? formatFiatCurrency({
        value: Number(revenue24hFiatAmountWithoutMinus || 0),
        fiatCode: fiatCode,
        showDecimals: true,
      })
    : 0

  return (
    <BaseView
      row
      align="center"
      gap={responsiveAdapter(6)}
      px={responsiveAdapter(6)}
      py={responsiveAdapter(2)}
      rounded={responsiveAdapter(20)}
      style={{
        backgroundColor: isProfit
          ? theme.colors.background.profitFlag
          : theme.colors.background.loseFlag,
      }}
    >
      {/* Revenue amount */}
      {Number(revenue24hFiatAmount) !== 0 && (
        <BaseView row align="center" gap={responsiveAdapter(2)}>
          <BaseText
            fontSize={12}
            fontFamily="bold"
            color={isProfit ? 'label.flagProfit' : 'label.default'}
          >
            {isProfit ? '+' : '-'}
          </BaseText>
          <BaseText
            fontSize={12}
            fontFamily="bold"
            color={isProfit ? 'label.flagProfit' : 'label.default'}
          >
            {formattedRevenue24hFiatAmount}
          </BaseText>
        </BaseView>
      )}

      {/* Percentage change with arrow */}
      <BaseView row align="center">
        <AppIcons
          name={isProfit ? 'smallArrowUp' : 'smallArrowDown'}
          size={responsiveAdapter(16)}
          color={isProfit ? 'label.flagProfit' : 'label.default'}
        />
        <BaseText
          fontSize={12}
          fontFamily="bold"
          color={isProfit ? 'label.flagProfit' : 'label.default'}
        >
          {handledPercentage}%
        </BaseText>
      </BaseView>
    </BaseView>
  )
}

// Avatar component - as in RN
function CardAvatar({
  uri,
  path,
  name,
  size = 48,
  iconName,
  iconSize = 24,
  containerStyle,
  imageStyle,
}: CardAvatarProps) {
  return (
    <Avatar
      uri={uri}
      path={path}
      name={name || ''}
      size={size}
      iconName={iconName}
      iconSize={iconSize}
      containerStyle={containerStyle}
      imageStyle={imageStyle}
    />
  )
}

// AvatarGradient component
function CardAvatarGradient({
  name,
  iconName,
  colors = ['#6366F1', '#8B5CF6'],
  size = 48,
  iconSize = 24,
  style,
  start = [0, 0],
  end = [1, 1],
  borderRadiusRatio = 2,
}: CardAvatarGradientProps) {
  const adaptedSize = responsiveAdapter(size)
  const adaptedIconSize = responsiveAdapter(iconSize)

  return (
    <BaseView
      style={{
        width: `${adaptedSize}px`,
        height: `${adaptedSize}px`,
        borderRadius: `${adaptedSize / borderRadiusRatio}px`,
        background: `linear-gradient(${Math.atan2(end[1] - start[1], end[0] - start[0]) * (180 / Math.PI)}deg, ${colors[0]}, ${colors[1]})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
    >
      {iconName && (
        <AppIcons
          name={iconName}
          size={adaptedIconSize}
          color="label.buttonPrimaryEnabled"
        />
      )}
      {name && !iconName && (
        <BaseText
          fontSize={Math.floor(adaptedSize * 0.4)}
          fontFamily="bold"
          color="label.buttonPrimaryEnabled"
        >
          {name.charAt(0).toUpperCase()}
        </BaseText>
      )}
    </BaseView>
  )
}

// Export as compound component
export const CardItem = {
  Root: CardRoot,
  Content: CardContent,
  Footer: CardFooter,
  CurrencyIcon: CardCurrencyIcon,
  Title: CardTitle,
  Description: CardDescription,
  RightIcon: CardRightIcon,
  FlagProfitsOrLosses: CardFlagProfitsOrLosses,
  Avatar: CardAvatar,
  AvatarGradient: CardAvatarGradient,
}
